import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { useApiMutation } from '../../api/mutations';
import { apiKeysFixture, createApiKeyFixture, rotateApiKeyFixture } from './fixture';
import { createDebugLogger } from '../../utils/debug';

const dlog = createDebugLogger('api-keys');

/**
 * Normalisiert eine API-Key-Row.
 *
 * Toleriert verschiedene Drift-Muster:
 *   - scopes als Array oder String (',', ' '-getrennt) oder undefined
 *   - status als string ('active') oder als boolean (`active: true`)
 *     oder via Timestamp (`revokedAt: null` = aktiv)
 *   - prefix unter verschiedenen Field-Namen
 *   - createdAt/lastUsedAt/expiresAt optional
 */
function normalizeApiKey(k) {
  if (!k) return null;

  let status = k.status;
  if (!status) {
    if (k.revokedAt) status = 'revoked';
    else if (k.expiresAt && new Date(k.expiresAt) < new Date()) status = 'expired';
    else status = 'active';
  }

  let scopes = k.scopes ?? k.scope ?? [];
  if (typeof scopes === 'string') {
    scopes = scopes.split(/[,\s]+/).filter(Boolean);
  }

  return {
    id: k.id,
    label: k.label ?? k.name ?? k.description ?? `Key #${k.id}`,
    prefix: k.prefix ?? k.keyPrefix ?? k.tokenPrefix ?? null,
    scopes: Array.isArray(scopes) ? scopes : [],
    status,
    lastUsedAt: k.lastUsedAt ?? k.lastUsed ?? null,
    expiresAt: k.expiresAt ?? k.expires ?? null,
    createdAt: k.createdAt ?? k.created ?? null,
    revokedAt: k.revokedAt ?? null,
    raw: k,
  };
}

function normalizeApiKeysList(raw) {
  if (!raw) return { keys: [], total: 0 };
  // Häufige Wrapper-Variationen
  const list = raw.keys ?? raw.apiKeys ?? raw.items ?? raw.data ?? (Array.isArray(raw) ? raw : []);
  return {
    keys: list.map(normalizeApiKey).filter(Boolean),
    total: raw.total ?? raw.totalCount ?? list.length,
    raw,
  };
}

/** GET /v1/admin/api-keys */
export function useApiKeys() {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: ['apiKeys', fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return normalizeApiKeysList(apiKeysFixture());
      }
      const res = await fetch('/v1/admin/api-keys');
      dlog('/v1/admin/api-keys raw', res);
      return normalizeApiKeysList(res);
    },
    staleTime: 30_000,
  });
}

/** POST /v1/admin/api-keys — returns plaintext exactly once. */
export function useCreateApiKey() {
  return useApiMutation({
    live: (fetch, payload) =>
      fetch('/v1/admin/api-keys', { method: 'POST', body: payload }),
    fixture: (payload) => createApiKeyFixture(payload),
    invalidate: ['apiKeys'],
    // Kein Toast — Plaintext zeigt das OneShotSecretReveal-Modal
  });
}

/** DELETE /v1/admin/api-keys/:id — revoke (irreversible, aber rolesafe) */
export function useRevokeApiKey() {
  return useApiMutation({
    live: (fetch, { id }) =>
      fetch(`/v1/admin/api-keys/${id}`, { method: 'DELETE' }),
    invalidate: ['apiKeys'],
    successMessage: 'API-Key wurde revoked.',
  });
}

/** POST /v1/admin/api-keys/:id/rotate-secret — neues Plaintext, alter sofort ungültig */
export function useRotateApiKey() {
  return useApiMutation({
    live: (fetch, { id }) =>
      fetch(`/v1/admin/api-keys/${id}/rotate-secret`, { method: 'POST' }),
    fixture: ({ id }) => rotateApiKeyFixture(id),
    invalidate: ['apiKeys'],
    // Kein Toast — neuer Plaintext zeigt das OneShotSecretReveal-Modal
  });
}
